/**
 * @file WmsReceiptEditPage.tsx
 * @description Карточка приёмки (phase13.0; позиции переведены на фасовки в
 * phase19.13 этап 0; плитки meta и «Движения по документу» — phase19.23, A6;
 * единый шаблон документа — phase19.24 этап 1, шаг 1; причина приёмки, срок
 * «мягко» и печать этикеток — phase19.22).
 *
 * DRAFT (причина, позиции) → «Провести» (блок внутреннего выпуска без парного
 * списания, предупреждение о сроке W20) → COMMITTED → «Этикетки» → «Разместить».
 * Раскладка — `DocumentLayout`, части — компоненты `Receipt*`, запросы — хуки;
 * странице остаются права и «что сейчас открыто».
 *
 * phase19.31 (зона «Склад», минимум по SC50): шапка — в верхней панели
 * (`ReceiptDocHeader` → `WmsDocumentPageHeader`: «назад», номер, статус,
 * «⋯», одно главное действие); своя липкая полоса «Провести приёмку» убрана —
 * на телефоне действие в общей нижней полосе каркаса. Кладовщик цену продажи
 * не видит (SC51): ни в форме строки, ни в строках документа.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useCurrentContext, useHasPermission } from "../../../auth/hooks";
import { Alert } from "../../../components/ui/alert";
import { DocumentLayout } from "../../../components/ui/DocumentLayout";
import { SectionLabel } from "../../../components/ui/SectionLabel";
import { pendingLabelItems, receiptLabelOf } from "../../../components/wms/labels/receipt-print-items";
import { useReceiptLabels } from "../../../components/wms/labels/useReceiptLabels";
import { ReceiptBulkAddButton } from "../../../components/wms/ReceiptBulkAddButton";
import { ReceiptDocHeader } from "../../../components/wms/ReceiptDocHeader";
import { ReceiptDraftForm } from "../../../components/wms/ReceiptDraftForm";
import { ReceiptFooter } from "../../../components/wms/ReceiptFooter";
import { ReceiptItemForm } from "../../../components/wms/ReceiptItemForm";
import { ReceiptItemsList } from "../../../components/wms/ReceiptItemsList";
import { ReceiptNotices } from "../../../components/wms/ReceiptNotices";
import { ReceiptPageDialogs, type ReceiptRowEditing } from "../../../components/wms/ReceiptPageDialogs";
import { useDocumentRowHighlight } from "../../../components/wms/useDocumentRowHighlight";
import { useReceiptCommit } from "../../../components/wms/useReceiptCommit";
import { useReceiptDocument } from "../../../components/wms/useReceiptDocument";
import type { GoodsReceiptItemDto } from "../../../lib/api-wms";
import { canSeeSalePrice } from "../../../lib/sale-price-visibility";

export function WmsReceiptEditPage() {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const navigate = useNavigate();
  const isNew = id === "new" || !id;
  // Хуки прав зовём безусловно: `||` между вызовами — это условный хук.
  const canPick = useHasPermission("SHOP_WMS_PICKING");
  const canManageWms = useHasPermission("SHOP_WMS_MANAGE");
  const canViewWms = useHasPermission("SHOP_WMS_VIEW");
  const canReceive = useHasPermission("SHOP_WMS_RECEIVE");
  const canPlace = canPick || canManageWms; // лист размещения (OQ-46)
  const canPrint = canViewWms || canManageWms; // печать этикеток (wms-labels.md «Права»)
  const canSetExpiry = canReceive || canManageWms; // «Указать срок» (W20)
  const highlight = useDocumentRowHighlight("receipt-item");
  // Цену продажи не видит контекст с одними складскими правами (SC51, W11,
  // контракт §11) — то же правило, что у сервера.
  const showSalePrice = canSeeSalePrice(useCurrentContext());

  const doc = useReceiptDocument(id, isNew);
  const { receipt } = doc;
  const commit = useReceiptCommit({ receipt, reload: doc.reload, setReceipt: doc.setReceipt });
  const committed = receipt?.status === "COMMITTED";
  const labels = useReceiptLabels(receipt?.id, committed);
  const [printOpen, setPrintOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Очередь строк для ввода срока: «⋯ → Указать срок» — одна, «Заполнить сроки» — все.
  const [expiryQueue, setExpiryQueue] = useState<string[]>([]);
  // Б16: правка строки черновика («Изменить позицию» / «Указать ячейку»).
  const [rowEditing, setRowEditing] = useState<ReceiptRowEditing | null>(null);

  if (doc.loading) {
    return <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin text-primary" /></div>;
  }
  if (isNew) {
    return (
      <ReceiptDraftForm
        backTo={`/shop/${slug}/wms/receipts`}
        onCreated={(created) => navigate(`/shop/${slug}/wms/receipts/${created.id}`)}
      />
    );
  }
  if (doc.error || !receipt) {
    return <Alert severity="error">{doc.error ?? "Приёмка не найдена"}</Alert>;
  }

  const isDraft = receipt.status === "DRAFT";
  const items = receipt.items ?? [];
  const missing = receipt.missingExpiryItems ?? [];
  const expiryItem = items.find((it) => it.id === expiryQueue[0]) ?? null;
  const addedSkuIds = items.map((it) => it.skuId);
  const openPrint = () => setPrintOpen(true);
  const openHistory = () => setHistoryOpen(true);
  const goToSku = (skuId: string) => {
    const row = items.find((it) => it.skuId === skuId);
    if (row) highlight.goTo(row.id);
  };

  return (
    <DocumentLayout
      header={
        <ReceiptDocHeader
          receipt={receipt}
          backTo={`/shop/${slug}/wms/receipts`}
          pendingLabels={canPrint ? pendingLabelItems(labels.status) : 0}
          canPlace={canPlace}
          committing={commit.committing}
          onCommit={() => void commit.commit()}
          onPrintLabels={canPrint ? openPrint : undefined}
          blockReason={commit.blocks[0]?.message ?? null}
        />
      }
      notices={
        <ReceiptNotices
          receipt={receipt}
          labels={labels.status}
          canPrint={canPrint}
          onPrint={openPrint}
          onOpenHistory={openHistory}
          onReasonChange={(reason, parentId) => void doc.updateReason(reason, parentId)}
          blocks={commit.blocks}
        />
      }
      main={
        <section className="flex min-w-0 flex-col gap-2">
          <SectionLabel
            as="h2"
            count={items.length}
            trailing={
              isDraft ? (
                <ReceiptBulkAddButton
                  receiptId={receipt.id}
                  alreadyAddedIds={addedSkuIds}
                  onAdded={() => void doc.reload(receipt.id)}
                />
              ) : undefined
            }
          >
            Позиции
          </SectionLabel>
          <ReceiptItemsList
            items={items}
            highlightedId={highlight.highlightedId}
            rowDomId={highlight.rowDomId}
            editable={isDraft}
            onRemove={(itemId) => void doc.removeItem(itemId)}
            missingExpiryIds={missing}
            labelOf={committed ? receiptLabelOf(labels.status) : undefined}
            onSetExpiry={canSetExpiry ? (item: GoodsReceiptItemDto) => setExpiryQueue([item.id]) : undefined}
            showSalePrice={showSalePrice}
            onEdit={(item) => setRowEditing({ kind: "edit", item })}
            onSetBin={(item) => setRowEditing({ kind: "bin", item })}
          />
        </section>
      }
      aside={
        isDraft ? (
          <ReceiptItemForm
            existingItems={items}
            onGoToExisting={goToSku}
            warehouseId={receipt.warehouseId}
            submitting={doc.adding}
            onSubmit={doc.addItem}
            showSalePrice={showSalePrice}
          />
        ) : undefined
      }
      footer={!isDraft ? <ReceiptFooter receipt={receipt} labels={labels} canPrint={canPrint} /> : null}
      dialogs={
        <ReceiptPageDialogs
          receipt={receipt}
          labels={labels}
          commit={commit}
          printOpen={printOpen}
          onPrintOpenChange={setPrintOpen}
          historyOpen={historyOpen}
          onHistoryOpenChange={setHistoryOpen}
          expiryItem={expiryItem}
          expiryProgress={expiryQueue.length > 1 ? `ещё ${expiryQueue.length - 1}` : undefined}
          onExpiryClose={() => setExpiryQueue([])}
          onSetExpiry={doc.setItemExpiry}
          onDraftDates={doc.updateItemDates}
          onExpirySaved={() => {
            const next = expiryQueue.slice(1);
            setExpiryQueue(next);
            if (next[0]) highlight.goTo(next[0]);
          }}
          onFillExpiry={(itemIds) => {
            if (itemIds[0]) highlight.goTo(itemIds[0]);
            setExpiryQueue(itemIds);
          }}
          onPrinted={() => void doc.reload(receipt.id)}
          rowEdit={{ editing: rowEditing, onClose: () => setRowEditing(null), onUpdate: doc.updateItem, showSalePrice }}
        />
      }
    />
  );
}
